import { useTranslation } from 'react-i18next';
import type { DomainErrorInfo } from '../../components/error-alert';
import { isLeadStatus, isNeedsRuleReason, isRuleField, isSettingsField } from './labels';

/** Words the lead router's machine codes for `ErrorAlert`'s `describe`; other codes keep the shared wording. */
export function useLeadErrorText(): (domain: DomainErrorInfo) => string | undefined {
  const { t } = useTranslation(['leads']);
  // The status a lead moved to (LEAD_NOT_RETRYABLE / _REROUTABLE details); `gone` when it was deleted.
  const statusText = (status: unknown) =>
    isLeadStatus(status) ? t(`general.leadStatus.${status}`) : t('received.errors.gone');
  return (domain) => {
    switch (domain.code) {
      case 'LEAD_RULE_INVALID': {
        const field = domain.details?.field;
        return isRuleField(field)
          ? t('errors.invalidField', { field: t(`editor.field.${field}`) })
          : t('errors.invalid');
      }
      case 'LEAD_RULE_EXISTS':
        return t('errors.exists');
      case 'LEAD_RULE_NOT_FOUND':
        return t('errors.notFound');
      case 'LEAD_OPTION_LIMIT':
        return t('errors.optionLimit');
      case 'LEAD_OPTION_CREATE_FAILED':
        return t('errors.optionCreateFailed');
      case 'PIPEDRIVE_NOT_CONFIGURED':
        return t('errors.pipedriveNotConfigured');
      case 'LEAD_SETTINGS_STALE':
        return t('general.errors.settingsStale');
      case 'LEAD_SETTINGS_INVALID': {
        const field = domain.details?.field;
        if (field === 'fallbackOwnerUserPdId') return t('general.errors.fallbackInactive');
        return isSettingsField(field)
          ? t('general.errors.settingsInvalidField', { field: t(`general.settings.field.${field}`) })
          : t('general.errors.settingsInvalid');
      }
      case 'LEAD_CHANNEL_NOT_FOUND':
        return t('general.errors.channelNotFound');
      case 'LEAD_CHANNEL_KIND':
        return t('general.errors.channelKind');
      case 'LEAD_CHANNEL_DEFAULT_INCOMPLETE': {
        const market = domain.details?.marketCode;
        return t('general.errors.defaultIncomplete', { market: typeof market === 'string' ? market : '' });
      }
      case 'LEAD_CHANNEL_NOT_SHADOW':
        return t('general.errors.notShadow');
      case 'LEAD_CHANNEL_DISABLED':
        return t('general.errors.channelDisabled');
      case 'LEAD_CHANNEL_NO_PAGE':
        return t('general.errors.noPage');
      case 'LEAD_META_NOT_CONFIGURED':
        return t('general.errors.metaNotConfigured');
      case 'LEAD_TEST_RATE_LIMITED':
        return t('general.errors.testRateLimited');
      case 'META_PERMISSION_DENIED': {
        const scope = domain.details?.missingScope;
        return typeof scope === 'string' && scope
          ? t('general.errors.metaPermissionScope', { scope })
          : t('general.errors.metaPermission');
      }
      case 'LEAD_ACCOUNT_NOT_META':
        return t('general.errors.accountNotMeta');
      case 'LEAD_ACCOUNT_NOT_FOUND':
        return t('general.errors.accountNotFound');
      case 'LEAD_ACCOUNT_INVALID':
        return t('general.errors.accountInvalid');
      // --- tab "Received leads" and the dry run ---
      case 'LEAD_NOT_RETRYABLE':
        return t('received.errors.notRetryable', { status: statusText(domain.details?.status) });
      case 'LEAD_NOT_REROUTABLE':
        return t('received.errors.notReroutable', { status: statusText(domain.details?.status) });
      case 'LEAD_DELIVERY_ACTIVE':
        return t('received.errors.deliveryActive');
      case 'LEAD_CURSOR_INVALID':
        return t('received.errors.cursorInvalid');
      case 'LEAD_NOT_FOUND':
        return t('received.errors.notFound');
      case 'LEAD_ROUTING_CONTENDED':
        return t('received.errors.contended');
      case 'LEAD_PERSON_SEARCH_FAILED':
        return t('received.errors.personSearch');
      case 'LEAD_DRY_RUN_INVALID':
        return t('received.errors.dryRunInvalid');
      default:
        return undefined;
    }
  };
}

/** The reason a `needs_rule` lead waits, worded (the API stores the engine's reason code). */
export function useNeedsRuleText(): (reason: string | null) => string {
  const { t } = useTranslation(['leads']);
  return (reason) => (isNeedsRuleReason(reason) ? t(`received.needsRuleReason.${reason}`) : (reason ?? '—'));
}
